import React from 'react';
import { TypedMemo } from '../../../common/types';
import { inlineMediaUrl } from '../inlineMediaUrl';
import { AssetViewerItem } from './viewerItems';
import { SpotifyHeaderViewerMedia } from '../studio/spotifyHeader/SpotifyHeaderViewerMedia';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the viewer; each part uses its own slice of it */
import styles from './AssetViewer.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ViewerMediaProps {
  item: AssetViewerItem;
  label: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onVideoError: () => void;
}

/**
 * The file at full size (designs a1, a2). An image is the file itself. A video plays in the page
 * with the browser controls; the API file route answers 206 to a range, so a seek does not load
 * the whole file. The motion artwork plays its H.264 preview. A loop asset loops. The parent gives
 * each item its own element (`key`), so a move stops the video.
 */
const ViewerMediaComponent: React.FC<ViewerMediaProps> = ({ item, label, videoRef, onVideoError }) => {
  if (item.mediaKind === 'image' && item.themeId === 'spotifyHeader') {
    // The Spotify header shows as Spotify shows it, with the name and the photo (design spotify-header a4).
    return <SpotifyHeaderViewerMedia src={inlineMediaUrl(item.fileUrl)} label={label} fileClassName={styles.file} />;
  }
  if (item.mediaKind === 'image') {
    return <img className={styles.file} src={inlineMediaUrl(item.fileUrl)} alt={label} data-testid="viewer-image" />;
  }
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption -- a made asset has no speech track to caption
    <video
      ref={videoRef}
      className={styles.file}
      src={inlineMediaUrl(item.playUrl)}
      poster={item.posterUrl === null ? undefined : inlineMediaUrl(item.posterUrl)}
      aria-label={label}
      controls
      playsInline
      preload="metadata"
      loop={item.loop}
      onError={onVideoError}
      data-testid="viewer-video"
    />
  );
};

export const ViewerMedia = TypedMemo(ViewerMediaComponent);
